import {toast} from '@wandb/common/components/elements/Toast';
import {generatePNGDataURL} from '@wandb/common/util/panelExport';
import copyToClipboard from 'copy-to-clipboard';

import {PreviewableLinkResourceType} from '../../generated/graphql';
import {createPreviewableLink} from '../../util/previewableLinks';

type GeneratePreviewableReportLinkProps = {
  userProvidedPreviewImageURL?: string; // URL of image to use as link preview image
  panelDomEl: HTMLElement | null; // DOM element corresponding to panel to be used as link preview image
  entityName: string; // Name of associated entity
  title?: string; // Report title
  description?: string; // Report description
  viewID: string; // viewID of report
  shouldCopyToClipboard?: boolean; // Whether to copy link to clipboard after generating
  redirectURL: string; // URL to actual report; will redirect here after following link
  author?: string; // Report author
};

export const generatePreviewableReportLink = async ({
  userProvidedPreviewImageURL,
  panelDomEl,
  entityName,
  title,
  description,
  viewID,
  shouldCopyToClipboard,
  redirectURL,
  author = 'Weights & Biases',
}: GeneratePreviewableReportLinkProps) => {
  let imageUrl = '';
  if (userProvidedPreviewImageURL) {
    imageUrl = userProvidedPreviewImageURL;
  } else {
    if (panelDomEl == null) {
      console.error('Could not generate link - DOM element not found');
      return;
    }
    try {
      imageUrl = await generatePNGDataURL(panelDomEl);
    } catch (e) {
      console.error(e);
    }
    if (!imageUrl) {
      console.error('Could not generate link - imageURL is empty or null');
      return;
    }
  }

  try {
    // eslint-disable-next-line wandb/no-unprefixed-urls
    const res = await fetch(imageUrl);
    const imageBlob = await res.blob();
    const previewableLink = await createPreviewableLink({
      previewImage: imageBlob,
      entityName,
      title: title ?? '',
      description: description ?? '',
      resourceID: viewID,
      resourceType: PreviewableLinkResourceType.View,
      redirectURL,
      author,
      useDefaultImage: false,
    });
    if (shouldCopyToClipboard) {
      if (copyToClipboard(previewableLink)) {
        toast('Link copied to clipboard');
      } else {
        toast('Error copying link to clipboard - please try again');
      }
    }
    return previewableLink;
  } catch (e) {
    console.error(e);
  }
  return;
};
